import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Heart } from 'lucide-react';

export const FinalCTA = ({ onContinue }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section
      ref={ref}
      dir="rtl"
      className="relative py-16 sm:py-20 px-6 overflow-hidden bg-gradient-to-br from-brand-50 via-secondary-50 to-brand-100"
    >
      {/* Soft animated glow blobs */}
      <motion.div
        className="pointer-events-none absolute top-10 right-20 w-72 h-72 bg-brand-200/40 rounded-pill blur-3xl"
        animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.55, 0.35] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute bottom-20 left-20 w-96 h-96 bg-secondary-200/40 rounded-pill blur-3xl"
        animate={{ scale: [1, 1.25, 1], opacity: [0.25, 0.45, 0.25] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      <div className="relative max-w-3xl mx-auto text-center">
        {/* Icon medallion */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, ease: [0.34, 1.4, 0.64, 1] }}
          className="flex justify-center mb-7"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-secondary-400/30 rounded-pill blur-2xl" />
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-pill bg-surface/95 backdrop-blur-sm flex items-center justify-center shadow-elevated ring-4 ring-surface">
              <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-secondary-500" fill="currentColor" strokeWidth={1.5} />
            </div>
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <h2 className="display-2 mb-4 leading-tight">
            מוכנים להצטרף לעולם של<br />חיבורים ובני אדם?
          </h2>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="text-lead text-ink-muted mb-10 max-w-xl mx-auto"
        >
          הקהילה שלכם מחכה לגלות אתכם.
        </motion.p>

        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="surface-glass rounded-2xl p-6 sm:p-8 mb-10 max-w-2xl mx-auto text-right"
        >
          <ul className="space-y-3.5">
            {[
              'גלו עסקים ואנשים מדהימים ממש ליד הבית',
              'תמכו בכלכלה המקומית וחזקו את השכונה',
              'היו חלק מקהילה אמיתית ותומכת',
              'צרו חיבורים משמעותיים עם שכנים',
            ].map((benefit, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: 16 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.45, delay: 0.45 + i * 0.08 }}
                className="flex items-center gap-3"
              >
                <span aria-hidden="true" className="shrink-0 w-2 h-2 rounded-pill bg-brand-500" />
                <span className="text-base sm:text-lg text-ink font-medium">{benefit}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.85 }}
          className="flex flex-col items-center gap-3"
        >
          <motion.button
            type="button"
            onClick={onContinue}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            className={[
              'inline-flex items-center justify-center px-10 py-4 rounded-xl',
              'bg-brand-500 text-ink-inverse font-bold text-xl sm:text-2xl',
              'shadow-elevated hover:bg-brand-600 hover:shadow-xl',
              'transition-[background-color,box-shadow,transform] duration-200 ease-smooth',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
            ].join(' ')}
          >
            כן! אני רוצה להצטרף
          </motion.button>

          <p className="text-sm text-ink-muted">ההצטרפות חינמית ולוקחת שנייה</p>
        </motion.div>

        {/* Decorative dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-12 flex justify-center gap-2.5"
        >
          {[...Array(5)].map((_, i) => (
            <motion.span
              key={i}
              aria-hidden="true"
              animate={{ scale: [1, 1.25, 1], opacity: [0.4, 0.8, 0.4] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
              className="w-2 h-2 rounded-pill bg-brand-500"
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
